import React, { useState } from 'react';
import { styled } from 'linaria/react';
import { elementPressAnimation } from '../ui/animations/elementPressAnimation';
import { cx } from 'linaria';
import { useLightOrDark } from '../../utils/useLightOrDark';
import { ReactComponent as CheckIcon } from '../../svg/Checkmark.svg';

const ElevateVariantsWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 5px;
  z-index: 2;
`;

const RemainingSwatchCount = styled('span')`
  text-box-trim: trim-both;
  color: #333;
  font-size: 14px;
  font-weight: 600;
`;

const SWATCHES_TO_SHOW = 5;

export const ElevateVariants = ({
  variantProducts,
  selectedVariantKey,
  onVariantChange,
  shouldSlice = true,
  shouldSort = false,
  showName = false,
  className
}) => {
  // if (variantProducts?.length === 1) {
  //   return null;
  // }
  const handleOnSelect = colors => {
    // find the variant with the color
    const variant = variantProducts?.find(variant => {
      return variant?.swatch?.colors === colors;
    });
    onVariantChange?.(variant);
  };

  let swatchesToShow = shouldSlice
    ? variantProducts?.slice(0, SWATCHES_TO_SHOW)
    : variantProducts;

  // Sort the swatches by the key number
  if (shouldSort) {
    swatchesToShow = swatchesToShow?.sort((a, b) => {
      if (a.key > b.key) return 1;
      if (a.key < b.key) return -1;
      return 0;
    });
  }
  const remainingSwatchCount = variantProducts?.length - SWATCHES_TO_SHOW;

  return (
    <ElevateVariantsWrapper
      className={cx('elevate-variants-wrapper', className)}
    >
      {swatchesToShow?.map((variant, index) => (
        <div
          key={index + '-elevate-variant-' + variant?.key}
          className="elevate-variant-item-wrapper"
        >
          <ColorSwatch
            colors={variant?.swatch?.colors}
            colorNames={variant?.custom?.color}
            selectedColor={selectedVariantKey === variant?.key}
            onSelect={handleOnSelect}
            showName={showName}
          />
        </div>
      ))}
      {shouldSlice && remainingSwatchCount > 0 && (
        <RemainingSwatchCount>+{remainingSwatchCount}</RemainingSwatchCount>
      )}
    </ElevateVariantsWrapper>
  );
};

const ColorVariantButton = styled('button')`
  border: 1px solid #e0e0e0;
  background: ${({ gradient }) => gradient || 'none'};
  cursor: pointer;
  padding: 0;
  margin: 0;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  overflow: visible;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    max-width: 8px;
    max-height: 8px;
    height: 8px;
    color: ${props => props.svgcolor};
    filter: drop-shadow(
      0 0 3px
        ${props =>
          props.svgcolor === '#000'
            ? 'rgba(255,255,255,0.95)'
            : '#rgba(0,0,0,0.95)'}
    );
  }
  .hover {
    position: absolute;
    top: auto;
    bottom: 125%;
    left: -4px;
    width: max-content;
    height: fit-content;
    background: #fff;
    font-size: 14px;
    font-weight: 600;
    padding: 2px 4px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  }

  &.show-name {
    margin-bottom: 30px;
    svg {
      max-width: 14px;
      max-height: 14px;
      height: 14px;
      color: ${props => props.svgcolor};
      filter: drop-shadow(
        0 0 3px
          ${props =>
            props.svgcolor === '#000'
              ? 'rgba(255,255,255,0.95)'
              : '#rgba(0,0,0,0.95)'}
      );
    }

    .elevate-variant-name {
      position: absolute;
      top: 120%;
      left: -4px;
      width: max-content;
      height: fit-content;
      background: #fff;
      font-size: 14px;
      font-weight: 600;
      padding: 2px 4px;
    }
  }

  /* Selection ring uses box-shadow: global button:focus sets outline: none !important,
   which strips outline-based rings while the swatch stays focused after click. */
  &.selected {
    border: 2px solid white;
    box-shadow: 0 0 0 1px #000;
  }
`;

const getGradientFromColors = colors => {
  if (!Array.isArray(colors) || colors.length === 0) return 'none';
  if (colors.length === 1) return colors[0];
  const colorStops = colors
    .map((color, idx, arr) => {
      const pct = Math.round((idx / (arr.length - 1)) * 100);
      return `${color} ${pct}%`;
    })
    .join(', ');
  // Use linear gradient, could also choose radial-gradient if you wish
  // To create a "cake parts" (pie-like) appearance, use a conic-gradient:
  // To create sharp, distinct color pie parts (no fading), set abrupt color stops for each color segment.
  const sharpColorStops = colors
    .map((color, idx, arr) => {
      const segmentSize = 100 / arr.length;
      const startPct = idx * segmentSize;
      const endPct = (idx + 1) * segmentSize;
      // Each color is only between its segment
      return `${color} ${startPct}% ${endPct}%`;
    })
    .join(', ');
  return `conic-gradient(${sharpColorStops})`;
};

const ColorSwatch = ({
  colors = [],
  colorNames = [],
  selectedColor,
  onSelect,
  showName
}) => {
  const gradient = getGradientFromColors(colors);
  const { multiColors } = useLightOrDark(190);
  const svgColor = multiColors(colors);
  const colorName = colorNames?.map(color => color?.label).join(', ');
  const [hovering, setHovering] = useState(false);

  return (
    <ColorVariantButton
      svgcolor={svgColor === 'light' ? '#000' : '#fff'}
      style={{ '--press-scale': '1.2' }}
      className={cx(
        elementPressAnimation,
        selectedColor && 'selected',
        showName && 'show-name'
      )}
      type="button"
      gradient={gradient}
      aria-label={`Select color ${colorName}`} // TODO: translade and add color name
      onClick={e => {
        // Prevent the surrounding product link from navigating.
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(colors);
      }}
      onMouseEnter={() => {
        setHovering(true);
      }}
      onMouseLeave={() => {
        setHovering(false);
      }}
    >
      {selectedColor && (
        <>
          <CheckIcon />
          {showName && <div className="elevate-variant-name">{colorName}</div>}
        </>
      )}
      {hovering && (
        <div className="elevate-variant-name hover">{colorName}</div>
      )}
    </ColorVariantButton>
  );
};
